import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Divider } from "@/components/Divider";
import BrandStripe from "@/components/BrandStripe";
import Logo from "@/components/Logo";

export default function LandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/landing-pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setErr("Page not found"));
  }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center text-center px-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-black">Page not found.</h1>
          <Link to="/" className="mt-6 inline-block text-[#00A651] font-bold uppercase text-sm tracking-wider">
            ← Back home
          </Link>
        </div>
      </div>
    );
  }
  if (!page) return <div className="min-h-screen grid place-items-center">Loading…</div>;

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="page-landing">
      <BrandStripe thickness="h-1.5" />
      <header className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Logo size={36} /></Link>
          <a href={`tel:+15551234567`} className="text-xs uppercase tracking-wider font-bold">Call (555) 123-4567</a>
        </div>
      </header>

      <main className="flex-1">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            {page.geo_city && (
              <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-[#00A651]">
                <MapPin size={14} /> {page.geo_city}
              </p>
            )}
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-black mt-3 leading-[1.05] tracking-tight">
              {page.headline}
            </h1>
            <Divider className="my-6" />
            <p className="font-sub italic text-xl text-gray-700 max-w-2xl">{page.subheadline}</p>
            <p className="text-gray-700 mt-5 leading-relaxed max-w-2xl">{page.body}</p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to={`/apply?program=${page.program}&source=${page.slug}`}
                data-testid="landing-apply"
                className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008f45] text-white font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-colors"
              >
                {page.cta_label} <ArrowRight size={16} />
              </Link>
              <Link
                to="/calculator"
                className="inline-flex items-center gap-2 bg-white border-2 border-[#F26522] hover:bg-[#F26522] hover:text-white text-[#F26522] font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-colors"
              >
                Estimate Payment
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-black text-white p-8 rounded-sm border-t-4 border-t-[#E88C2F]">
              <ShieldCheck className="text-[#00A651]" size={28} />
              <p className="text-xs uppercase tracking-[0.25em] text-[#E88C2F] font-bold mt-4">{page.target_keyword}</p>
              <h3 className="font-heading font-bold text-2xl mt-2">{page.program.toUpperCase()} program</h3>
              <ul className="mt-5 space-y-3 text-sm text-gray-300">
                <li>• Licensed, NMLS-registered originators</li>
                <li>• Decisions in 24 hours</li>
                <li>• No surprise fees at closing</li>
                <li>• Equal Housing Lender</li>
              </ul>
              <Link
                to={`/apply?program=${page.program}&source=${page.slug}`}
                className="mt-7 block text-center bg-[#00A651] hover:bg-[#008f45] text-white font-bold uppercase tracking-wider text-sm px-6 py-3 rounded-sm transition-colors"
              >
                Apply in 10 minutes
              </Link>
            </div>
          </div>
        </section>
      </main>

      <BrandStripe thickness="h-1.5" />
    </div>
  );
}
